import { Button, Card, Group, SimpleGrid, Stack, Text } from '@mantine/core'
import { ReactNode, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  AcceptedFileTypes,
  UploadDropzone
} from '@/components/datasets/UploadDropzone/UploadDropzone'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { formatSize } from '@/utils/file'
import { TestModelMediaCard } from './TestModelMediaCard'

const MAXIMUM_FILES = 5
const MAX_FILE_SIZE_BYTES = 100 * 1024 ** 2 // 100MB

const UPLOAD_ACCEPTED_FILE_TYPES: AcceptedFileTypes = [
  'jpeg',
  'png',
  'mp4',
  'mov'
]

type DropzoneFileListProps = {
  files: File[]
  onFileDelete: (fileName: string) => void
}

const DropzoneFileList = ({ files, onFileDelete }: DropzoneFileListProps) => {
  return (
    <SimpleGrid cols={{ xs: 2, lg: 7 }} spacing="md">
      {files.map((file, index) => (
        <TestModelMediaCard
          key={index}
          file={file}
          onFileDelete={() => onFileDelete(file.name)}
        />
      ))}
    </SimpleGrid>
  )
}

type TestModelUploadMediaProps = {
  isInferencePending: boolean
  leftSection?: ReactNode
  isStage2?: boolean
  onStartInference: (files: File[]) => void
}

export const TestModelUploadMedia = ({
  isInferencePending,
  leftSection,
  isStage2,
  onStartInference
}: TestModelUploadMediaProps) => {
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])

  const totalSize = useMemo(() => {
    return acceptedFiles.reduce((acc, file) => acc + file.size, 0)
  }, [acceptedFiles])

  const handleDrop = (files: File[]) => {
    setAcceptedFiles([...files, ...acceptedFiles])
  }

  const handleFileRemove = (fileName: string) => {
    const newFiles = acceptedFiles.filter((file) => file.name !== fileName)
    setAcceptedFiles(newFiles)
  }

  return (
    <>
      <Stack>
        {acceptedFiles.length > 0 ? (
          <DropzoneFileList
            files={acceptedFiles}
            onFileDelete={handleFileRemove}
          />
        ) : (
          <>
            <PlatformAlert variant="info" showIcon={false}>
              <FormattedMessage id="models.test.upload.instructions" />
            </PlatformAlert>

            {isStage2 && (
              <PlatformAlert
                variant="warning"
                showIcon={false}
                title={
                  <FormattedMessage id="models.test.stage2.upload.instructions.title" />
                }
              >
                <FormattedMessage id="models.test.stage2.upload.instructions.description" />
              </PlatformAlert>
            )}
          </>
        )}

        {totalSize > 0 && (
          <Text size="xs" c="dimmed">
            {formatSize(totalSize, 'b', true)}
          </Text>
        )}

        <UploadDropzone
          disabled={acceptedFiles.length >= MAXIMUM_FILES || isInferencePending}
          acceptedFileTypes={UPLOAD_ACCEPTED_FILE_TYPES}
          fileLimit={MAXIMUM_FILES}
          maxSize={MAX_FILE_SIZE_BYTES}
          minHeight={480}
          showInstructions
          onDrop={handleDrop}
          onReject={() => {}}
        />

        <Card withBorder>
          <Group justify="space-between">
            <div>{leftSection}</div>

            <Button
              miw={160}
              disabled={acceptedFiles.length === 0}
              loading={isInferencePending}
              onClick={() => onStartInference(acceptedFiles)}
            >
              <FormattedMessage id="continue" />
            </Button>
          </Group>
        </Card>
      </Stack>
    </>
  )
}
